import type { Location } from 'react-router-dom';
import { CREATOR_PATHS, isCreatorStudioPath } from '../config/creatorProgramPaths';

export type LoginRedirectState = {
  from?: Location;
  message?: string;
};

const AUTH_ENTRY_PATHS = ['/login', '/register', '/forgot-password', '/account/set-password'] as const;

function isAuthEntryPath(pathname: string): boolean {
  if (AUTH_ENTRY_PATHS.includes(pathname as (typeof AUTH_ENTRY_PATHS)[number])) {
    return true;
  }
  return (
    pathname.startsWith('/reset-password') ||
    pathname.startsWith('/activate/') ||
    pathname === '/account/set-password'
  );
}

/**
 * Parse router location.state from a redirect to /login.
 */
export function getLoginRedirectState(state: unknown): LoginRedirectState {
  if (!state || typeof state !== 'object') return {};
  const candidate = state as LoginRedirectState;
  return {
    from: candidate.from,
    message: candidate.message,
  };
}

/**
 * Build a safe in-app return path from a router Location (open-redirect safe).
 */
export function buildReturnPathFromLocation(from: Location | undefined): string | null {
  if (!from?.pathname) return null;

  const path = `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;

  if (!path.startsWith('/') || path.startsWith('//')) return null;
  if (isAuthEntryPath(from.pathname)) return null;

  return path;
}

/**
 * Build a safe in-app return path from a string (localStorage checkout redirect, etc.).
 */
export function buildReturnPathFromString(path: string | null | undefined): string | null {
  if (!path?.trim()) return null;

  const trimmed = path.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;

  const pathname = trimmed.split(/[?#]/)[0];
  if (isAuthEntryPath(pathname)) return null;

  return trimmed;
}

export type PostLoginRedirectInput = {
  locationState: unknown;
  storedAuthRedirect: string | null;
  userRoles?: string[];
  currentPathname: string;
};

/**
 * Priority after successful login:
 * 1. Explicit checkout/design redirect (localStorage)
 * 2. Original protected URL (router state.from)
 * 3. Owner default dashboard when no specific destination
 */
export function resolvePostLoginRedirect({
  locationState,
  storedAuthRedirect,
  userRoles,
  currentPathname,
}: PostLoginRedirectInput): string | null {
  const fromStorage = buildReturnPathFromString(storedAuthRedirect);
  if (fromStorage && fromStorage !== currentPathname) {
    return fromStorage;
  }

  const { from } = getLoginRedirectState(locationState);
  const fromProtectedRoute = buildReturnPathFromLocation(from);
  if (fromProtectedRoute && fromProtectedRoute !== currentPathname) {
    return fromProtectedRoute;
  }

  if (
    !fromProtectedRoute &&
    !fromStorage &&
    userRoles?.includes('owner') &&
    !currentPathname.startsWith('/owner')
  ) {
    return '/owner';
  }

  const isCreatorOnly =
    userRoles?.includes('creator') &&
    !userRoles?.includes('seller') &&
    !userRoles?.includes('owner');

  const isPublicShoppingPath =
    currentPathname === '/shop' || currentPathname.startsWith('/shop/');

  if (
    !fromProtectedRoute &&
    !fromStorage &&
    isCreatorOnly &&
    !isCreatorStudioPath(currentPathname) &&
    !isPublicShoppingPath
  ) {
    return CREATOR_PATHS.dashboard;
  }

  return fromProtectedRoute ?? fromStorage ?? null;
}
